import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogContent,
  keyframes,
  styled
} from '@mui/material';
import { 
  AutoAwesome, 
  Psychology, 
  Settings, 
  CheckCircle,
  RocketLaunch 
} from '@mui/icons-material';

// Animation keyframes
const pulse = keyframes`
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.1); opacity: 0.8; }
  100% { transform: scale(1); opacity: 1; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const shimmer = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: calc(200px + 100%) 0; }
`;

const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-20px); }
  60% { transform: translateY(-10px); }
`;

const progress = keyframes`
  0% { width: 0%; }
  100% { width: 100%; }
`;

// Styled components
const AnimatedIcon = styled(Box)(({ theme, delay = 0 }) => ({
  animation: `${float} 2s ease-in-out infinite`,
  animationDelay: `${delay}s`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 60,
  height: 60,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
  margin: '0 10px',
  '& svg': {
    color: 'white',
    fontSize: 28,
  }
}));

const ProgressBar = styled(Box)({
  width: '100%',
  height: 6,
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  borderRadius: 3,
  overflow: 'hidden',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    background: 'linear-gradient(90deg, #667eea, #764ba2, #667eea)',
    backgroundSize: '200px 100%',
    animation: `${progress} 3s ease-in-out infinite, ${shimmer} 2s infinite`,
  }
});

const CreateAgentLoader = ({ open, onClose, mode = 'create' }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);

  // Configuration based on mode
  const config = {
    create: {
      title: 'Creating Your AI Agent',
      subtitle: 'Please wait while we set up your intelligent assistant',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      steps: [
        {
          title: 'Initializing Agent',
          description: 'Setting up the foundation for your AI agent',
          icon: <Settings />,
          color: '#667eea'
        },
        {
          title: 'Loading AI Models',
          description: 'Preparing advanced machine learning models',
          icon: <Psychology />,
          color: '#764ba2'
        },
        {
          title: 'Configuring Intelligence',
          description: 'Fine-tuning the agent\'s cognitive capabilities',
          icon: <AutoAwesome />,
          color: '#f093fb'
        },
        {
          title: 'Launching Agent',
          description: 'Deploying your agent to the cloud',
          icon: <RocketLaunch />,
          color: '#f5576c'
        },
        {
          title: 'Agent Ready!',
          description: 'Your AI agent is successfully created',
          icon: <CheckCircle />,
          color: '#43e97b'
        }
      ]
    },
    deploy: {
      title: 'Deploying Your AI Agent',
      subtitle: 'Please wait while we deploy your agent to production',
      background: 'linear-gradient(135deg, #3B5CFF 0%, #2E4BC0 100%)',
      steps: [
        {
          title: 'Preparing Deployment',
          description: 'Setting up deployment environment',
          icon: <Settings />,
          color: '#3B5CFF'
        },
        {
          title: 'Building Container',
          description: 'Creating optimized deployment package',
          icon: <Psychology />,
          color: '#2E4BC0'
        },
        {
          title: 'Uploading to Cloud',
          description: 'Transferring agent to production servers',
          icon: <AutoAwesome />,
          color: '#1E3A8A'
        },
        {
          title: 'Starting Services',
          description: 'Initializing agent services and APIs',
          icon: <RocketLaunch />,
          color: '#1E40AF'
        },
        {
          title: 'Deployment Complete!',
          description: 'Your agent is now live and ready to use',
          icon: <CheckCircle />,
          color: '#10B981'
        }
      ]
    }
  };

  const currentConfig = config[mode];
  const steps = currentConfig.steps;

  useEffect(() => {
    if (open) {
      const stepInterval = setInterval(() => {
        setCurrentStep((prev) => {
          if (prev < steps.length - 1) {
            return prev + 1;
          } else {
            clearInterval(stepInterval);
            return prev;
          }
        });
      }, 2000);

      const progressInterval = setInterval(() => {
        setProgress((prev) => {
          if (prev < 100) {
            return prev + 2;
          } else {
            clearInterval(progressInterval);
            return prev;
          }
        });
      }, 60);

      return () => {
        clearInterval(stepInterval);
        clearInterval(progressInterval);
      };
    } else {
      setCurrentStep(0);
      setProgress(0);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: currentConfig.background,
          borderRadius: 4,
          overflow: 'hidden',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)',
            backgroundSize: '200px 100%',
            animation: `${shimmer} 3s infinite`,
          }
        }
      }}
    >
      <DialogContent sx={{ p: 4, textAlign: 'center', color: 'white' }}>
        {/* Header */}
        <Typography
          variant="h4"
          sx={{
            fontWeight: 'bold',
            mb: 1,
            background: 'linear-gradient(45deg, #fff, #f0f0f0)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            animation: `${pulse} 2s ease-in-out infinite`
          }}
        >
          {currentConfig.title}
        </Typography>

        <Typography
          variant="body1"
          sx={{
            opacity: 0.9,
            mb: 4,
            fontSize: '16px'
          }}
        >
          {currentConfig.subtitle}
        </Typography>

        {/* Progress Steps */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            {steps.map((step, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                <AnimatedIcon
                  delay={index * 0.2}
                  sx={{
                    background: index <= currentStep 
                      ? step.color 
                      : 'rgba(255, 255, 255, 0.2)',
                    animation: index === currentStep ? `${bounce} 1s infinite` : `${float} 2s ease-in-out infinite`,
                    animationDelay: `${index * 0.2}s`,
                  }}
                >
                  {step.icon}
                </AnimatedIcon>
                {index < steps.length - 1 && (
                  <Box
                    sx={{
                      width: 40,
                      height: 2,
                      background: index < currentStep 
                        ? step.color 
                        : 'rgba(255, 255, 255, 0.2)',
                      mx: 1,
                      transition: 'all 0.3s ease'
                    }}
                  />
                )}
              </Box>
            ))}
          </Box>

          {/* Current Step Info */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 'bold',
                mb: 1,
                animation: `${pulse} 2s ease-in-out infinite`
              }}
            >
              {steps[currentStep]?.title}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                opacity: 0.8,
                fontStyle: 'italic'
              }}
            >
              {steps[currentStep]?.description}
            </Typography>
          </Box>

          {/* Progress Bar */}
          <Box sx={{ mb: 2 }}>
            <ProgressBar />
            <Typography
              variant="body2"
              sx={{
                mt: 1,
                opacity: 0.8,
                fontWeight: 'bold'
              }}
            >
              {progress}% Complete
            </Typography>
          </Box>
        </Box>

        {/* Animated Particles */}
        <Box sx={{ position: 'relative', height: 60 }}>
          {[...Array(6)].map((_, index) => (
            <Box
              key={index}
              sx={{
                position: 'absolute',
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.6)',
                animation: `${float} 3s ease-in-out infinite`,
                animationDelay: `${index * 0.5}s`,
                left: `${20 + index * 15}%`,
                top: '50%',
                transform: 'translateY(-50%)',
              }}
            />
          ))}
        </Box>

        {/* Status Message */}
        <Typography
          variant="body2"
          sx={{
            mt: 2,
            opacity: 0.7,
            fontStyle: 'italic',
            animation: `${pulse} 3s ease-in-out infinite`
          }}
        >
          This may take a few moments...
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

export default CreateAgentLoader;
